/**
 * CircuitBreakerManager — Protección contra servicios degradados
 *
 * Implementa el patrón Circuit Breaker para servicios de geocodificación:
 * - CLOSED:    servicio disponible, se registran fallos consecutivos
 * - OPEN:      servicio deshabilitado tras N fallos consecutivos
 * - HALF_OPEN: prueba controlada tras cooldown temporal
 *
 * Problema resuelto (F9.01a-fix):
 *   CDAU-WFS health check pasa (HEAD 200) pero queries GET reales
 *   fallan (ERR_NETWORK). Cada timeout consume ~8 s del presupuesto
 *   e impide que CartoCiudad/Nominatim resuelvan infraestructuras.
 *
 * @module services/geocoding/orchestrator/CircuitBreakerManager
 * @version 1.0.0 — F9.01a-fix
 */

// ============================================================================
// TIPOS
// ============================================================================

/** Estado del circuito por servicio */
export type CircuitState = 'CLOSED' | 'OPEN' | 'HALF_OPEN';

/** Configuración del circuit breaker */
export interface CircuitBreakerConfig {
  /** Fallos consecutivos para abrir circuito (default: 2) */
  failureThreshold: number;
  /** Milisegundos antes de pasar a HALF_OPEN (default: 30_000) */
  halfOpenAfterMs: number;
  /** Éxitos en HALF_OPEN para cerrar circuito (default: 1) */
  successesToClose: number;
}

/** Estado interno por servicio */
interface ServiceCircuit {
  state: CircuitState;
  consecutiveFailures: number;
  consecutiveSuccessesInHalfOpen: number;
  lastFailureTime: number | null;
  totalFailures: number;
  totalSkips: number;
}

/** Estadísticas públicas de un servicio */
export interface CircuitStats {
  state: CircuitState;
  consecutiveFailures: number;
  totalFailures: number;
  totalSkips: number;
  lastFailureTime: number | null;
}

// ============================================================================
// CONFIGURACIÓN POR DEFECTO
// ============================================================================

const DEFAULT_CONFIG: CircuitBreakerConfig = {
  failureThreshold: 2,
  halfOpenAfterMs: 30_000,
  successesToClose: 1,
};

// ============================================================================
// CIRCUIT BREAKER MANAGER
// ============================================================================

/**
 * Gestiona circuit breakers para múltiples servicios de geocodificación.
 *
 * Uso típico:
 * ```ts
 * const cb = new CircuitBreakerManager({ failureThreshold: 2 });
 *
 * if (cb.isAvailable('CDAU_WFS')) {
 *   try {
 *     const result = await cdauGeocoder.geocode(opts);
 *     cb.recordSuccess('CDAU_WFS');
 *     return result;
 *   } catch (err) {
 *     cb.recordFailure('CDAU_WFS');
 *     // Continuar cascada
 *   }
 * } else {
 *   // Servicio deshabilitado → skip inmediato, ahorro ~8 s
 *   attempts.push('cdau:skipped_circuit_open');
 * }
 * ```
 */
export class CircuitBreakerManager {
  private readonly config: CircuitBreakerConfig;
  private readonly circuits = new Map<string, ServiceCircuit>();
  private nowFn: () => number;

  constructor(config?: Partial<CircuitBreakerConfig>, nowFn?: () => number) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.nowFn = nowFn ?? (() => Date.now());
  }

  // --------------------------------------------------------------------------
  // API PÚBLICA
  // --------------------------------------------------------------------------

  /**
   * ¿Está el servicio disponible para recibir peticiones?
   *
   * - CLOSED → true
   * - HALF_OPEN → true (permitir prueba controlada)
   * - OPEN → false, salvo que haya pasado el cooldown → transición a HALF_OPEN
   */
  isAvailable(serviceKey: string): boolean {
    const circuit = this.circuits.get(serviceKey);
    if (!circuit) return true; // No registrado = disponible

    if (circuit.state === 'CLOSED') return true;
    if (circuit.state === 'HALF_OPEN') return true;

    // OPEN: comprobar si pasó el cooldown
    if (circuit.state === 'OPEN' && circuit.lastFailureTime !== null) {
      const elapsed = this.nowFn() - circuit.lastFailureTime;
      if (elapsed >= this.config.halfOpenAfterMs) {
        circuit.state = 'HALF_OPEN';
        circuit.consecutiveSuccessesInHalfOpen = 0;
        return true;
      }
    }

    // OPEN y no ha pasado cooldown → skip
    circuit.totalSkips++;
    return false;
  }

  /** Registra éxito de un servicio */
  recordSuccess(serviceKey: string): void {
    const circuit = this.getOrCreate(serviceKey);
    
    if (circuit.state === 'HALF_OPEN') {
      circuit.consecutiveSuccessesInHalfOpen++;
      if (circuit.consecutiveSuccessesInHalfOpen >= this.config.successesToClose) {
        // Suficientes éxitos → cerrar circuito
        circuit.state = 'CLOSED';
        circuit.consecutiveFailures = 0;
        circuit.consecutiveSuccessesInHalfOpen = 0;
      }
    } else {
      // En CLOSED: resetear contador de fallos
      circuit.consecutiveFailures = 0;
    }
  }

  /** Registra fallo de un servicio */
  recordFailure(serviceKey: string): void {
    const circuit = this.getOrCreate(serviceKey);
    circuit.consecutiveFailures++;
    circuit.totalFailures++;
    circuit.lastFailureTime = this.nowFn();

    if (circuit.state === 'HALF_OPEN') {
      // Fallo en prueba controlada → volver a OPEN
      circuit.state = 'OPEN';
      circuit.consecutiveSuccessesInHalfOpen = 0;
      return;
    }

    // CLOSED: comprobar umbral
    if (circuit.consecutiveFailures >= this.config.failureThreshold) {
      circuit.state = 'OPEN';
    }
  }

  /** Estado actual de un servicio */
  getState(serviceKey: string): CircuitState {
    return this.circuits.get(serviceKey)?.state ?? 'CLOSED';
  }

  /** Estadísticas de todos los servicios registrados */
  getAllStats(): Map<string, CircuitStats> {
    const stats = new Map<string, CircuitStats>();
    for (const [key, circuit] of this.circuits) {
      stats.set(key, {
        state: circuit.state,
        consecutiveFailures: circuit.consecutiveFailures,
        totalFailures: circuit.totalFailures,
        totalSkips: circuit.totalSkips,
        lastFailureTime: circuit.lastFailureTime,
      });
    }
    return stats;
  }

  /** Estadísticas de un servicio concreto */
  getStats(serviceKey: string): CircuitStats | null {
    const circuit = this.circuits.get(serviceKey);
    if (!circuit) return null;
    return {
      state: circuit.state,
      consecutiveFailures: circuit.consecutiveFailures,
      totalFailures: circuit.totalFailures,
      totalSkips: circuit.totalSkips,
      lastFailureTime: circuit.lastFailureTime,
    };
  }

  /** Reinicia todos los circuitos */
  reset(): void {
    this.circuits.clear();
  }

  /** Reinicia un servicio concreto */
  resetService(serviceKey: string): void {
    this.circuits.delete(serviceKey);
  }

  /** Lista de servicios actualmente abiertos (deshabilitados) */
  getOpenServices(): string[] {
    return [...this.circuits.entries()]
      .filter(([, c]) => c.state === 'OPEN')
      .map(([key]) => key);
  }

  /** Resumen compacto para logging */
  getSummary(): string {
    const entries: string[] = [];
    for (const [key, c] of this.circuits) {
      if (c.state !== 'CLOSED' || c.totalFailures > 0) {
        entries.push(`${key}:${c.state}(f=${c.totalFailures},s=${c.totalSkips})`);
      }
    }
    return entries.length > 0 ? entries.join(' | ') : 'all_healthy';
  }

  // --------------------------------------------------------------------------
  // PRIVADO
  // --------------------------------------------------------------------------

  private getOrCreate(serviceKey: string): ServiceCircuit {
    let circuit = this.circuits.get(serviceKey);
    if (!circuit) {
      circuit = {
        state: 'CLOSED',
        consecutiveFailures: 0,
        consecutiveSuccessesInHalfOpen: 0,
        lastFailureTime: null,
        totalFailures: 0,
        totalSkips: 0,
      };
      this.circuits.set(serviceKey, circuit);
    }
    return circuit;
  }
}
